/**
 * Helpers for a requirement's form, shared by the Requirements tab (which has
 * to know whether a ticked card still needs answers) and the modal (which
 * edits them).
 *
 * The modal works in a DRAFT: field id to the string its control holds. The
 * event stores ANSWERS: an array that carries each question's label with it.
 * The conversion happens here and nowhere else, the same arrangement the tab
 * uses for ticked ids and stored requirements.
 */
import {
	EventRequirementAnswer,
	RequirementField,
} from "../types/event";
import { toDatePart, toIso, toTimePart } from "./eventDates";

/** Field id to the value its control holds. */
export type RequirementDraft = Record<string, string>;

/** Every field that takes an answer, which is all of them but guidance. */
export function answerableFields(fields: RequirementField[]): RequirementField[] {
	return fields.filter((f) => f.type !== "info");
}

/**
 * The fields on screen for a draft: those with no `ShowIf`, or whose parent
 * answer currently equals it. A field whose parent is itself hidden is hidden
 * too, so a chain of conditions cannot leave a child showing under nothing.
 * A condition that points at a missing or later field never matches.
 */
export function visibleFields(
	fields: RequirementField[],
	draft: RequirementDraft,
): RequirementField[] {
	const shown = new Set<string>();
	const out: RequirementField[] = [];
	for (const field of fields) {
		const cond = field.showIf;
		if (
			cond &&
			!(shown.has(cond.id) && (draft[cond.id] ?? "").trim() === cond.equals)
		) {
			continue;
		}
		shown.add(field.id);
		out.push(field);
	}
	return out;
}

/** The draft with the answers to hidden fields removed. */
export function pruneHidden(
	fields: RequirementField[],
	draft: RequirementDraft,
): RequirementDraft {
	const keep = new Set(visibleFields(fields, draft).map((f) => f.id));
	const next: RequirementDraft = {};
	for (const [id, value] of Object.entries(draft)) {
		// Ids the form no longer asks about are left for draftToAnswers to drop.
		if (keep.has(id) || !fields.some((f) => f.id === id)) next[id] = value;
	}
	return next;
}

/**
 * A `datetime` field is two controls, a CCDateField and a CCField
 * `type="time"`, so its draft is the two parts joined by `T`. Either side can
 * be empty while someone is halfway through, which a single ISO instant
 * cannot represent: choosing a time before a date would lose the time.
 */
export function splitDateTime(draft: string | undefined): {
	date: string;
	time: string;
} {
	const [date = "", time = ""] = (draft ?? "").split("T");
	return { date, time };
}

export function joinDateTime(date: string, time: string): string {
	return date || time ? `${date}T${time}` : "";
}

/** Stored answers to the modal's draft. */
export function answersToDraft(
	answers: EventRequirementAnswer[] | null | undefined,
): RequirementDraft {
	const draft: RequirementDraft = {};
	for (const answer of answers ?? []) {
		draft[answer.Id] =
			answer.Type === "datetime"
				? joinDateTime(toDatePart(answer.Value), toTimePart(answer.Value))
				: answer.Value;
	}
	return draft;
}

/**
 * The modal's draft to what the event stores.
 *
 * Only fields on the form TODAY and currently shown (an answer to a hidden
 * field is dropped, so a stale one cannot stay attached), and only those with
 * something in them. An
 * answer to a question since removed from the dictionary is dropped on the
 * next save rather than carried forever under a label nobody is asking.
 */
export function draftToAnswers(
	fields: RequirementField[],
	draft: RequirementDraft,
): EventRequirementAnswer[] {
	const answers: EventRequirementAnswer[] = [];
	for (const field of answerableFields(visibleFields(fields, draft))) {
		if (field.type === "info") continue;
		const raw = (draft[field.id] ?? "").trim();
		let value = raw;
		if (field.type === "datetime") {
			const { date, time } = splitDateTime(raw);
			// A date with no time is still an answer (midnight), but a time
			// with no date is not one at all.
			value = date ? toIso(date, time) : "";
		}
		if (!value) continue;
		answers.push({
			Id: field.id,
			Label: field.label,
			Type: field.type,
			Value: value,
		});
	}
	return answers;
}

/**
 * The required questions still unanswered in a draft, as field id to message.
 *
 * A datetime needs BOTH parts: "set up by" without a time is the one question
 * the AV team cannot plan around.
 */
export function draftErrors(
	fields: RequirementField[],
	draft: RequirementDraft,
): Record<string, string> {
	const errors: Record<string, string> = {};
	// A hidden field is not asked, so it cannot be owed.
	for (const field of answerableFields(visibleFields(fields, draft))) {
		if (!field.required) continue;
		const raw = (draft[field.id] ?? "").trim();
		if (field.type === "datetime") {
			const { date, time } = splitDateTime(raw);
			if (!date || !time) errors[field.id] = "Choose a date and a time.";
		} else if (!raw) {
			errors[field.id] =
				field.type === "select" ? "Choose an option." : "This is required.";
		}
	}
	return errors;
}

/**
 * Whether a ticked requirement still owes required answers.
 *
 * Checked against the STORED answers, so a card shows "Details needed" until
 * the form has actually been saved, not merely opened.
 */
export function needsDetails(
	fields: RequirementField[],
	answers: EventRequirementAnswer[] | null | undefined,
): boolean {
	return Object.keys(draftErrors(fields, answersToDraft(answers))).length > 0;
}
